
import { auth } from '@/firebase';
import type * as firebase from 'firebase/app';
import { ROOT_ACTIONS } from '@/store/actions';
import { user } from '@/store/user';
import { USER_ACTIONS } from '@/store/user/actions';
import { Component, Vue } from 'vue-property-decorator';

interface AuthError {
  code?: string;
  message?: string;
  resolver?: firebase.default.auth.MultiFactorResolver;
}

/** Dutch message for a failed sign-in; Identity Platform hides whether the email or the password was wrong. */
function authErrorMessage(error: AuthError): string {
  // Firebase 8 predates INVALID_LOGIN_CREDENTIALS: it reports auth/internal-error with the server's reason in the message.
  if (error.message?.includes('INVALID_LOGIN_CREDENTIALS')) return 'E-mailadres of wachtwoord is onjuist.';
  switch (error.code) {
    case 'auth/invalid-login-credentials':
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'E-mailadres of wachtwoord is onjuist.';
    case 'auth/invalid-email':
      return 'Vul een geldig e-mailadres in.';
    case 'auth/user-disabled':
      return 'Dit account is uitgeschakeld.';
    case 'auth/too-many-requests':
      return 'Te veel mislukte pogingen. Probeer het later opnieuw.';
    case 'auth/network-request-failed':
      return 'Geen verbinding. Controleer je internet en probeer opnieuw.';
    case 'auth/invalid-verification-code':
      return 'De verificatiecode is onjuist.';
    case 'auth/code-expired':
      return 'De verificatiecode is verlopen. Log opnieuw in.';
    case 'auth/missing-email':
      return 'Vul je e-mailadres in.';
    default:
      // Unknown cases: show the code so it can be reported, instead of a message that explains nothing.
      return `Er ging iets mis (${error.code ?? 'onbekende fout'}). Probeer het opnieuw of neem contact op met een beheerder.`;
  }
}

@Component
export default class Login extends Vue {

  inputs = {
    email: '',
    password: '',
    rememberMe: false,
    phone: '',
    multiFactorVerificationCode: ''
  }

  loading = {
    submit: false,
  }

  step = 'login'

  /** Shown above the fields of the current step; cleared when the user types. */
  error = ''

  passwordResetSent = false

  multiFactorEnrollment = false
  multiFactorEnrollmentSent = false

  multiFactorSession = null as any;

  multiFactorResolver = null as any;

  /** The page asked for before logging in (?redirect=), only paths within the CMS. */
  get afterLogin(): string {
    const redirect = this.$route.query.redirect;
    if (typeof redirect !== 'string' || !redirect.startsWith('/') || redirect.startsWith('//') || redirect.startsWith('/\\')) return '/';
    return redirect;
  }

  async refreshEmailActivation() {
    let currentUser = auth().currentUser;

    if (currentUser) {
      await currentUser.reload()
      if (currentUser) {
        if (currentUser.emailVerified) {
          await this.submit();
        } else {
          this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
            color: 'error',
            text: 'Je email is nog niet geverifieerd'
          })
        }
      } else {
        this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
          color: 'error',
          text: 'Je bent niet ingelogd'
        })
      }
    } else {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: 'error',
        text: 'Je bent niet ingelogd'
      })
    }
  }
  
  phoneRule = (value: string) => {
    const phoneRegex = /^\+(32|31)\d{9}$/;
    return phoneRegex.test(value) || 'Telefoonnummer moet in het formaat +32912345678 of +31912345678 zijn';
  }

  async submit() {
    try {
      this.loading.submit = true;
      this.error = '';
      if (!this.inputs.email || !this.inputs.password) {
        this.error = 'Vul je e-mailadres en wachtwoord in.';
        return;
      }
      if (this.inputs.rememberMe) {
        await auth().setPersistence(auth.Auth.Persistence.LOCAL);
      }

      const userCredential = await auth().signInWithEmailAndPassword(this.inputs.email, this.inputs.password);


      if (userCredential.user) {
        if (!userCredential.user.emailVerified) {
          await userCredential.user.sendEmailVerification();
          this.step = 'email-verification'
        } else {
          await this.$store.dispatch(USER_ACTIONS.AUTHENTICATE_USER);
          await this.$router.push(this.afterLogin)
        }
      }

    } catch (e) {
      const error = e as AuthError;
      if (error.code == 'auth/multi-factor-auth-required' && error.resolver) {
        const resolver = error.resolver;
        this.multiFactorResolver = resolver;
        // The user is a multi-factor user. Second factor challenge is required.
        this.step = '2fa-verification'
        var phoneInfoOptions = {
          multiFactorHint: resolver.hints[0],
          session: resolver.session
        };
        var phoneAuthProvider = new auth.PhoneAuthProvider();
        this.verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, this.recaptchaVerifier)
        // ...
      } else {
        this.error = authErrorMessage(error);
        // Never persist the typed credentials. Identity Platform has its own
        // lockout and audit logs; writing the password (and MFA code) to
        // Firestore on a failed login exposed real credentials.
      }

    } finally {
      this.loading.submit = false;
    }
  }

  startPasswordReset() {
    this.error = '';
    this.passwordResetSent = false;
    this.step = 'password-reset';
  }

  backToLogin() {
    this.error = '';
    this.step = 'login';
  }

  /** Always ends on the same confirmation, so the page does not reveal which emails have an account. */
  async sendPasswordReset() {
    this.error = '';
    if (!this.inputs.email) {
      this.error = 'Vul je e-mailadres in.';
      return;
    }
    try {
      this.loading.submit = true;
      // Firebase's reset page links back here; the CMS domains are authorized in Identity Platform (infra/terraform).
      await auth().sendPasswordResetEmail(this.inputs.email, { url: `${window.location.origin}/login` });
      this.passwordResetSent = true;
    } catch (e) {
      const error = e as AuthError;
      if (error.code === 'auth/user-not-found') {
        this.passwordResetSent = true;
      } else {
        this.error = authErrorMessage(error);
      }
    } finally {
      this.loading.submit = false;
    }
  }

  verificationId = null as any;

  async submitMultiFactorEnrollmentSMS() {
    this.loading.submit = true;
    if (this.inputs.multiFactorVerificationCode.length === 0) {
      var phoneInfoOptions = {
        phoneNumber: this.inputs.phone,
        session: this.multiFactorSession
      };
      var phoneAuthProvider = new auth.PhoneAuthProvider();
      this.verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, this.recaptchaVerifier)
      this.multiFactorEnrollmentSent = true;
    } else {
      const cred = auth.PhoneAuthProvider.credential(this.verificationId, this.inputs.multiFactorVerificationCode);
      var multiFactorAssertion = auth.PhoneMultiFactorGenerator.assertion(cred);
      const user = auth().currentUser;
      if (user) {
        user.multiFactor.enroll(multiFactorAssertion, 'Phone');
      }

      await this.$store.dispatch(USER_ACTIONS.AUTHENTICATE_USER);
      this.$router.push(this.afterLogin)
    }
    this.loading.submit = false;
  }

  async submit2FAVerificationCode() {
    this.loading.submit = true;
    this.error = '';
    try {
      var cred = auth.PhoneAuthProvider.credential(this.verificationId, this.inputs.multiFactorVerificationCode);
      var multiFactorAssertion = auth.PhoneMultiFactorGenerator.assertion(cred);
      await this.multiFactorResolver.resolveSignIn(multiFactorAssertion);
      await this.$store.dispatch(USER_ACTIONS.AUTHENTICATE_USER);
      this.$router.push(this.afterLogin)
    } catch (e) {
      this.error = authErrorMessage(e as AuthError);
    } finally {
      this.loading.submit = false;
    }
  }

  recaptchaVerifier: any;
  mounted() {
    this.recaptchaVerifier = new auth.RecaptchaVerifier('recaptcha-container', {
      'size': 'invisible',
    });
  }
} 
